import React, { useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
    CategoryScale,
    Chart,
    LineElement,
    LinearScale,
    PointElement,
    Title
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// --- JITTER PLUGIN + OFFSET CALCULATOR ---

// Detect overlapping datasets by comparing their data arrays
function computeOverlapOffsets(datasets: { data: (number | null)[] }[]) {
    const keyMap = new Map<string, number[]>();
    const offsets: Record<number, number> = {};
    const pixelStep = 3; // vertical pixel separation

    datasets.forEach((ds, index) => {
        const key = JSON.stringify(ds.data ?? []);
        if (!keyMap.has(key)) keyMap.set(key, []);
        keyMap.get(key)!.push(index);
    });

    for (const overlappingGroup of keyMap.values()) {
        if (overlappingGroup.length <= 1) continue;

        const count = overlappingGroup.length;
        const start = -((count - 1) / 2) * pixelStep;

        overlappingGroup.forEach((datasetIndex, i) => {
            offsets[datasetIndex] = start + i * pixelStep;
        });
    }

    return offsets;
}

// Chart.js plugin that applies vertical jitter offsets during drawing
const jitterOverlapPlugin = {
    id: "jitterOverlap",
    beforeDatasetDraw(chart: any, args: any, options: any) {
        const offsets = chart?.config?.options?.plugins?.jitterOverlap?.offsets;
        if (!offsets) return;

        const offsetY = offsets[args.index];
        if (!offsetY) return;

        const { ctx } = chart;
        ctx.save();
        ctx.translate(0, offsetY); // apply vertical jitter
    },
    afterDatasetDraw(chart: any, args: any) {
        const offsets = chart?.config?.options?.plugins?.jitterOverlap?.offsets;
        if (!offsets) return;

        const offsetY = offsets[args.index];
        if (!offsetY) return;

        chart.ctx.restore(); // undo jitter
    }
};


export interface MultiLineChartJSProps {
    datasets: {
        label: string;
        data: (number | null)[];
        borderColor?: string;
        backgroundColor?: string;
        pointBorderColor?: string;
        spanGaps?: boolean;
    }[];
    labels: string[];
    title?: string;
    height?: string;
    width?: string;
    responsive?: boolean;
    maintainAspectRatio?: boolean;
    borderRadius?: string;
    backgroundColor?: string;
    horizontalScrolling?: boolean;
    pointRadius?: number;
    pointBorderWidth?: number;
    borderWidth?: number;
    pointFontSize?: number;
    fontFamily?: string;
    pointLabelOffset?: number;
    devicePixelRatio?: number;
    xLabelFontSize?: number;
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
    lineHeight?: number;
    showToolTips?: boolean;
    yAxisMax?: number;
    showDataLabels?: boolean;
    xAxisBorder?: boolean;
    yAxisBorder?: boolean;
    displayYTicks?: boolean;
    yStepSize?: number;
    firstAndLastYTickOnly?: boolean;
    xOffSet?: boolean;
    titleBottomMargin?: string;
    minimumPointDistance?: number;
}

const MultiLineChartJS: React.FC<MultiLineChartJSProps> = (props) => {
    const {
        showToolTips,
        lineHeight,
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        xLabelFontSize,
        pointLabelOffset,
        fontFamily,
        pointFontSize,
        horizontalScrolling,
        pointRadius,
        pointBorderWidth,
        borderWidth,
        labels,
        title,
        responsive,
        maintainAspectRatio,
        borderRadius,
        backgroundColor,
        height,
        width,
        datasets,
        yAxisMax,
        showDataLabels,
        xAxisBorder,
        yAxisBorder,
        displayYTicks,
        yStepSize,
        firstAndLastYTickOnly,
        xOffSet,
        titleBottomMargin = "12px",
        minimumPointDistance = 110
    } = props;

    const containerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.scrollLeft = container.scrollWidth;
        }
    }, []);

    Chart.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        ChartDataLabels,
        jitterOverlapPlugin
    );

    const chartData = {
        labels,
        datasets: datasets.map((ds, i) => ({
            label: ds.label ?? `Dataset ${i + 1}`,
            data: ds.data,
            borderColor: ds.borderColor ?? "#43B5F8",
            backgroundColor: ds.backgroundColor ?? "rgb(18, 22, 34)",
            pointBorderColor: ds.pointBorderColor ?? "#F7F9FC",
            pointRadius: pointRadius ?? 4,
            pointBorderWidth: pointBorderWidth ?? 2,
            borderWidth: borderWidth ?? 3,
            spanGaps: ds.spanGaps ?? false
        }))
    };

    // Compute jitter offsets for overlapping datasets
    const overlapOffsets = computeOverlapOffsets(chartData.datasets);

    const chartOptions = {
        responsive: responsive ?? true,
        maintainAspectRatio: maintainAspectRatio ?? true,
        devicePixelRatio: props.devicePixelRatio ? props.devicePixelRatio : 1,
        layout: {
            padding: {
                left: paddingLeft ?? 0,
                right: paddingRight ?? 0,
                top: paddingTop ?? 0,
                bottom: paddingBottom ?? 0
            }
        },
        scales: {
            x: {
                offset: xOffSet ?? false,
                ticks: {
                    display: true,
                    font: {
                        weight: "bold" as const,
                        size: xLabelFontSize,
                        ...(fontFamily && { family: fontFamily }),
                        ...(lineHeight && { lineHeight: lineHeight })
                    },
                    color: "#12162280"
                },
                border: { display: xAxisBorder ?? false },
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                suggestedMax: yAxisMax ?? 100,
                border: { display: yAxisBorder ?? false },
                ticks: {
                    display: displayYTicks ?? false,
                    ...(yStepSize ? { stepSize: yStepSize } : {}),
                    ...(firstAndLastYTickOnly ? {
                        callback: function (value: number | string) {
                            // Only show labels for 0 and 10
                            return value === 0 || value === 10 ? value : "";
                        },
                    } : {})
                },
                grid: {
                    display: true
                }
            }
        },
        plugins: {
            jitterOverlap: {
                offsets: overlapOffsets
            },
            tooltip: { enabled: showToolTips ?? false },
            datalabels: {
                display: showDataLabels ?? false,
                anchor: "end" as const,
                align: "top" as const,
                offset: pointLabelOffset,
                labels: {
                    title: {
                        font: {
                            weight: "bold" as const,
                            size: pointFontSize,
                            ...(fontFamily && { family: fontFamily })
                        },
                        color: "#121622"
                    }
                }
            },
            title: {
                display: !horizontalScrolling,
                text: title,
                align: "start" as const,
                padding: { top: 10, bottom: 15 },
                font: { weight: "bold" as const, size: 10 },
                color: "#121622"
            }
        }
    };

    const numberOfDataPoints = labels ? labels.length : 0;
    const totalWidth = numberOfDataPoints * minimumPointDistance;






    return (
        <>
            {horizontalScrolling ? (
                <>
                    <h3 style={{ marginBottom: titleBottomMargin }}>{title}</h3>
                    <div style={styles.wrapper}>
                        <div style={styles.chartWrapper} ref={containerRef}>
                            <div style={{ ...styles.chartContainer, width: `${totalWidth}px` }}>
                                <Line
                                    data={chartData}
                                    options={chartOptions}
                                />
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div
                    style={{
                        width: width,
                        height: height,
                        borderRadius: `${borderRadius ?? 0}`,
                        backgroundColor: backgroundColor
                    }}
                >
                    <Line options={chartOptions} data={chartData} />
                </div>
            )}
        </>
    );
};

const styles = {
    wrapper: {
        width: "100%",
        height: "299px",
        display: "flex",
        alignItems: "center"
    },
    chartWrapper: {
        width: "100%",
        height: "100%",
        overflowX: "auto" as const,
        overflowY: "hidden" as const
    },
    chartContainer: {
        height: "100%",
        minWidth: "100%"
    }
};

export default MultiLineChartJS;
